import React, { useState } from 'react';
import axios from 'axios';
import { Send, TrendingUp, Target, Users, Sparkles, Zap, RefreshCw, DollarSign, Repeat } from 'lucide-react';

// Founder Executive Suite · web Campaigns tab. Marketing ROI (CAC vs LTV per
// channel), referral velocity, and a Draft-with-AI announcement tool.
// Mirrors the mobile CampaignsTab. Consumes GET /api/founder/campaigns.
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const FOUNDER_API = `${BACKEND_URL}/api/founder`;

const C = {
  forest: '#1A4331', forestDeep: '#0F2D1F', cream: '#FAF6EC', creamWarm: '#F4EEDE',
  lime: '#B4D330', gold: '#C9A85B', leaf: '#6B9F4A', leafDeep: '#4E7A33', terracotta: '#C66B4A',
  sky: '#3F6C97', olive: '#8A6D2B',
  text: '#2D241E', textMuted: '#5C665C', textSoft: '#7A6B57', white: '#FFFFFF', sageDeep: '#D9E1CB',
  border: 'rgba(26,67,49,0.10)', leafTint: 'rgba(107,159,74,0.14)', goldTint: 'rgba(201,168,91,0.14)',
};
const OF = { fontFamily: 'Outfit, sans-serif' };
const cardCream = { background: C.cream, borderRadius: 20, border: `1px solid ${C.border}`, padding: 24, marginBottom: 20 };
const label = { fontSize: 11, fontWeight: 700, letterSpacing: '0.12em', color: C.textMuted, textTransform: 'uppercase' };
const money2 = (n) => `$${Number(n || 0).toLocaleString('en-NZ', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const money0 = (n) => `$${Number(n || 0).toLocaleString('en-NZ', { maximumFractionDigits: 0 })}`;

const TONE = { leaf: C.leafDeep, gold: C.gold, terracotta: C.terracotta };
const GOALS = [
  { key: 'growth', label: 'New growers' },
  { key: 'winback', label: 'Win-back' },
  { key: 'upgrade', label: 'Upgrade to Premium' },
  { key: 'referral', label: 'Referrals' },
];

export default function CampaignsTab({ data, token }) {
  const s = (data && data.summary) || {};
  const channels = (data && data.channels) || [];
  const series = (data && data.velocity_series) || [];
  const referral = (data && data.referral) || {};
  const best = data && data.best;

  const [draft, setDraft] = useState((data && data.draft) || null);
  const [channel, setChannel] = useState(best ? best.channel : (channels[0] && channels[0].channel) || 'instagram');
  const [goal, setGoal] = useState('growth');
  const [drafting, setDrafting] = useState(false);
  const [queued, setQueued] = useState(false);
  const [aiSource, setAiSource] = useState((data && data.draft) ? 'template' : null);

  const api = () => axios.create({ baseURL: FOUNDER_API, headers: { 'X-Founder-Token': token } });
  const chLabel = (channels.find((c) => c.channel === channel) || {}).label || channel;

  const runDraft = async (refresh) => {
    setDrafting(true); setQueued(false);
    try {
      const r = await api().post('/campaigns/draft', { channel, channel_label: chLabel, goal, refresh: !!refresh });
      setDraft(r.data); setAiSource(r.data.source || 'ai');
    } catch (e) { /* keep last draft */ }
    finally { setDrafting(false); }
  };

  const queueCampaign = async () => {
    try {
      await api().post('/action', { kind: 'marketing', channel, channel_label: chLabel, goal });
      setQueued(true);
    } catch (e) { /* noop */ }
  };

  if (!data) return null;
  const ratio = s.ltv_cac_ratio || 0;
  const maxVal = Math.max(1, ...channels.map((c) => Math.max(c.avg_ltv || 0, c.cac || 0)));
  const maxSignups = Math.max(1, ...series.map((p) => p.signups || 0));

  return (
    <div data-testid="campaigns-tab">
      {/* Hero: blended LTV:CAC */}
      <div style={{ background: C.forest, borderRadius: 20, padding: 24, marginBottom: 20 }} data-testid="campaigns-hero">
        <span style={{ fontSize: 12, fontWeight: 600, color: C.lime }}>Blended LTV : CAC</span>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, marginTop: 4 }}>
          <p style={{ ...OF, fontSize: 46, lineHeight: '50px', color: C.cream, margin: 0, fontWeight: 700 }} data-testid="campaigns-ltv-cac">{ratio}×</p>
          <span style={{ fontSize: 14, fontWeight: 600, color: ratio >= 3 ? C.lime : C.gold }}>{s.roi_pct >= 0 ? '+' : ''}{s.roi_pct || 0}% ROI</span>
        </div>
        <div style={{ display: 'flex', gap: 10, marginTop: 16, flexWrap: 'wrap' }}>
          <HeroPill label="Blended LTV" value={money2(s.blended_ltv)} />
          <HeroPill label="Blended CAC" value={money2(s.blended_cac)} />
          <HeroPill label="Payback" value={`${s.payback_months || 0} mo`} />
          <HeroPill label="ARPU" value={money2(s.arpu)} />
        </div>
      </div>

      {/* Signups / spend tiles */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 20 }}>
        <Tile Icon={Users} tone={C.forest} value={`${s.total_signups || 0}`} label="Attributed signups" />
        <Tile Icon={TrendingUp} tone={C.leafDeep} value={`+${s.signups_30d || 0}`} label="Signups · 30 days" />
        <Tile Icon={DollarSign} tone={C.olive} value={money0(s.modeled_spend_30d)} label="Modeled spend · 30d" />
        <Tile Icon={Target} tone={C.gold} value={`${ratio}×`} label="LTV : CAC" />
      </div>

      {/* CAC vs LTV per channel */}
      <div style={cardCream} data-testid="campaigns-channels">
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
          <p style={{ ...label, margin: 0 }}>CAC vs LTV by channel</p>
          <div style={{ display: 'flex', gap: 14 }}>
            <Legend color={C.leafDeep} text="LTV" />
            <Legend color={C.terracotta} text="CAC" />
          </div>
        </div>
        {channels.map((c, i) => (
          <div key={c.channel} style={{ padding: '14px 0', borderTop: i > 0 ? `1px solid ${C.border}` : 'none' }} data-testid={`campaign-channel-${c.channel}`}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
              <span style={{ fontSize: 14, fontWeight: 600, color: C.text }}>{c.label}</span>
              <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontSize: 11, fontWeight: 700, color: TONE[c.tone] || C.textMuted, background: 'rgba(26,67,49,0.05)', borderRadius: 999, padding: '3px 10px' }} data-testid={`campaign-ratio-${c.channel}`}>{c.ltv_cac_ratio}×</span>
                <span style={{ fontSize: 12, color: C.textMuted }}>{c.roi_pct >= 0 ? '+' : ''}{c.roi_pct}% · {c.payback_months}mo</span>
              </span>
            </div>
            <DualBar ltv={c.avg_ltv} cac={c.cac} maxVal={maxVal} />
            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 5 }}>
              <span style={{ fontSize: 12, color: C.leafDeep, fontWeight: 600 }}>LTV {money2(c.avg_ltv)}</span>
              <span style={{ fontSize: 12, color: C.textSoft }}>{c.users} signups · {c.active_rate}% active</span>
              <span style={{ fontSize: 12, color: C.terracotta, fontWeight: 600 }}>CAC {money2(c.cac)}</span>
            </div>
          </div>
        ))}
        {data.recommendation && (
          <div style={{ display: 'flex', gap: 8, marginTop: 14, background: C.leafTint, borderRadius: 12, padding: 14 }} data-testid="campaigns-recommendation">
            <Zap className="w-4 h-4 shrink-0" style={{ color: C.leafDeep, marginTop: 2 }} />
            <span style={{ fontSize: 13, color: C.text, lineHeight: 1.5 }}>{data.recommendation}</span>
          </div>
        )}
      </div>

      {/* Referral velocity */}
      <div style={cardCream} data-testid="campaigns-velocity">
        <p style={{ ...label, marginBottom: 12 }}>Signup velocity · 8 weeks</p>
        <div style={{ display: 'flex', alignItems: 'flex-end', gap: 8, height: 120 }}>
          {series.map((p, i) => (
            <div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6 }}>
              <span style={{ fontSize: 11, fontWeight: 600, color: C.textMuted }}>{p.signups}</span>
              <div style={{ width: '100%', height: Math.max(6, ((p.signups || 0) / maxSignups) * 90), background: i === series.length - 1 ? C.lime : C.leaf, borderRadius: 6, transition: 'height 0.5s ease' }} />
              <span style={{ fontSize: 10, color: C.textSoft }}>{p.label}</span>
            </div>
          ))}
        </div>
        <div style={{ display: 'flex', gap: 10, marginTop: 16, flexWrap: 'wrap' }}>
          <RefTile Icon={Repeat} label="Referrals completed" value={`${referral.completed || 0}`} sub={`${referral.conversion_pct || 0}% conversion`} />
          <RefTile Icon={Users} label="Pending" value={`${referral.pending || 0}`} sub="awaiting subscribe" />
          <RefTile Icon={TrendingUp} label="Last 7 days" value={`+${referral.last_7d || 0}`} sub="new referrals" />
        </div>
      </div>

      {/* AI Draft tool */}
      <div style={{ background: C.forestDeep, borderRadius: 20, padding: 24 }} data-testid="campaigns-ai-draft">
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Sparkles className="w-4 h-4" style={{ color: C.lime }} />
          <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.12em', color: C.lime }}>DRAFT A CAMPAIGN WITH AI</span>
        </div>
        <div style={{ display: 'flex', gap: 10, marginTop: 14, flexWrap: 'wrap' }}>
          <Select value={channel} onChange={setChannel} testid="campaign-channel-select"
            options={channels.map((c) => ({ value: c.channel, label: c.label }))} placeholder="Channel" />
          <Select value={goal} onChange={setGoal} testid="campaign-goal-select"
            options={GOALS.map((g) => ({ value: g.key, label: g.label }))} placeholder="Goal" />
          <button onClick={() => runDraft(!!draft)} disabled={drafting} data-testid="campaign-draft-button"
            style={{ display: 'flex', alignItems: 'center', gap: 8, background: C.lime, color: C.forestDeep, fontWeight: 700, padding: '10px 18px', borderRadius: 999, border: 'none', cursor: 'pointer', opacity: drafting ? 0.6 : 1 }}>
            {drafting ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
            {drafting ? 'Drafting…' : draft ? 'Regenerate' : 'Draft with AI'}
          </button>
        </div>

        {draft && (
          <div style={{ background: 'rgba(250,246,236,0.06)', borderRadius: 16, padding: 18, marginTop: 16 }} data-testid="campaign-draft-card">
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
              <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.1em', color: 'rgba(250,246,236,0.5)' }}>{(draft.channel_label || chLabel).toUpperCase()} · {draft.audience}</span>
              <span style={{ fontSize: 10, fontWeight: 700, color: aiSource === 'ai' ? C.lime : C.gold, background: 'rgba(255,255,255,0.08)', borderRadius: 999, padding: '2px 8px' }}>{aiSource === 'ai' ? 'AI' : 'DRAFT'}</span>
            </div>
            <p style={{ ...OF, fontSize: 20, fontWeight: 700, color: C.cream, margin: '0 0 6px' }} data-testid="campaign-draft-title">{draft.title}</p>
            <p style={{ fontSize: 14, color: 'rgba(250,246,236,0.82)', lineHeight: 1.55, margin: 0 }} data-testid="campaign-draft-message">{draft.message}</p>
            <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
              <button onClick={queueCampaign} disabled={queued} data-testid="campaign-queue-button"
                style={{ display: 'flex', alignItems: 'center', gap: 6, background: queued ? 'rgba(180,211,48,0.2)' : C.cream, color: queued ? C.lime : C.forestDeep, fontWeight: 700, padding: '10px 18px', borderRadius: 999, border: 'none', cursor: queued ? 'default' : 'pointer' }}>
                <Send className="w-4 h-4" /> {queued ? 'Queued ✓' : 'Queue campaign'}
              </button>
            </div>
          </div>
        )}
        <p style={{ fontSize: 11, color: 'rgba(250,246,236,0.4)', margin: '14px 0 0', lineHeight: 1.5 }}>{data.note}</p>
      </div>
    </div>
  );
}

function DualBar({ ltv, cac, maxVal }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
      <div style={{ height: 9, borderRadius: 5, background: C.sageDeep, overflow: 'hidden' }}>
        <div style={{ height: 9, borderRadius: 5, background: C.leafDeep, width: `${Math.max((ltv / maxVal) * 100, 3)}%`, transition: 'width 0.6s ease' }} />
      </div>
      <div style={{ height: 9, borderRadius: 5, background: C.sageDeep, overflow: 'hidden' }}>
        <div style={{ height: 9, borderRadius: 5, background: C.terracotta, width: `${Math.max((cac / maxVal) * 100, 3)}%`, transition: 'width 0.6s ease' }} />
      </div>
    </div>
  );
}

function Select({ value, onChange, options, testid, placeholder }) {
  return (
    <select value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid} aria-label={placeholder}
      style={{ background: 'rgba(250,246,236,0.10)', color: C.cream, border: '1px solid rgba(250,246,236,0.2)', borderRadius: 12, padding: '10px 14px', fontSize: 14, fontWeight: 600, outline: 'none', cursor: 'pointer' }}>
      {options.map((o) => <option key={o.value} value={o.value} style={{ color: C.text }}>{o.label}</option>)}
    </select>
  );
}

function HeroPill({ label: lbl, value }) {
  return (
    <div style={{ flex: 1, minWidth: 110, background: 'rgba(250,246,236,0.10)', borderRadius: 12, padding: 12 }}>
      <p style={{ fontSize: 12, color: C.cream, margin: 0, opacity: 0.8 }}>{lbl}</p>
      <p style={{ fontSize: 16, fontWeight: 600, color: C.cream, margin: 0 }}>{value}</p>
    </div>
  );
}
function Tile({ Icon, tone, value, label: lbl }) {
  return (
    <div style={{ background: C.cream, borderRadius: 14, border: `1px solid ${C.border}`, padding: 16 }}>
      <Icon className="w-4 h-4" style={{ color: tone }} />
      <p style={{ ...OF, fontSize: 24, fontWeight: 700, color: tone, margin: '4px 0 0' }}>{value}</p>
      <p style={{ fontSize: 12, color: C.textSoft, margin: 0 }}>{lbl}</p>
    </div>
  );
}
function RefTile({ Icon, label: lbl, value, sub }) {
  return (
    <div style={{ flex: 1, minWidth: 150, background: C.cream, borderRadius: 14, border: `1px solid ${C.border}`, padding: 14 }}>
      <Icon className="w-4 h-4" style={{ color: C.forest }} />
      <p style={{ ...OF, fontSize: 22, fontWeight: 700, color: C.forest, margin: '4px 0 0' }}>{value}</p>
      <p style={{ fontSize: 12, fontWeight: 600, color: C.text, margin: 0 }}>{lbl}</p>
      <p style={{ fontSize: 11, color: C.textSoft, margin: 0 }}>{sub}</p>
    </div>
  );
}
const Legend = ({ color, text }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
    <span style={{ width: 9, height: 9, borderRadius: 999, background: color }} />
    <span style={{ fontSize: 12, color: C.textMuted }}>{text}</span>
  </div>
);
